/**
 * TryDemoSection — landing page "Try the Live Demo" section.
 *
 * Two-card layout (Admin + Staff) with credentials, copy-to-clipboard, and
 * deep-links that auto-prefill the login page via ?demo=biz / ?demo=staff.
 *
 * Pure presentation — no data fetched. Stats are the actual seeded demo
 * numbers from /app/backend/scripts/seed_demo_business.py, so the section
 * stays accurate as long as the seed script is re-run.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Sparkles, Copy, Check, ArrowRight, Eye,
  Users, DollarSign, Clock, TrendingUp, Calendar, CheckSquare,
  BarChart3, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Small copy-to-clipboard chip ─────────────────────────────── */
function CredChip({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error('Copy failed — please copy manually');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="group w-full flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white/80 px-3 py-2 hover:border-orange-300 hover:bg-white transition-colors text-left"
      data-testid={testId}
    >
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
        <p className="font-mono text-sm text-slate-800 truncate">{value}</p>
      </div>
      <span
        className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
          copied
            ? 'bg-emerald-100 text-emerald-600'
            : 'bg-slate-100 text-slate-500 group-hover:bg-orange-50 group-hover:text-orange-600'
        }`}
        aria-hidden="true"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </span>
    </button>
  );
}

/* ─── Highlight pill ───────────────────────────────────────────── */
function Pill({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700">
      <Icon size={12} className="text-orange-500 shrink-0" />
      {text}
    </span>
  );
}

/* ─── Compact stat card ────────────────────────────────────────── */
function Stat({ icon: Icon, label, value, tone = 'orange' }) {
  const tones = {
    orange:  'text-orange-600 bg-orange-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    violet:  'text-violet-600 bg-violet-50',
    sky:     'text-sky-600 bg-sky-50',
  };
  return (
    <div className="flex items-center gap-2.5">
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${tones[tone]} shrink-0`}>
        <Icon size={16} />
      </span>
      <div className="min-w-0">
        <p className="text-base font-bold text-slate-900 leading-tight">{value}</p>
        <p className="text-[11px] text-slate-500 truncate">{label}</p>
      </div>
    </div>
  );
}

export default function TryDemoSection() {
  return (
    <section
      id="try-demo"
      className="relative py-10 md:py-24 overflow-hidden bg-gradient-to-b from-white via-orange-50/40 to-white"
      data-testid="try-demo-section"
    >
      {/* Decorative blurred blobs (lightweight, pure CSS) */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-emerald-200/20 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Eyebrow + heading */}
        <div className="text-center mb-8 md:mb-16">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            Live Demo · No Signup
          </span>
          <h2 className="mt-3 md:mt-4 text-[28px] md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
            See ReStaff in action — <span className="text-orange-500">instantly</span>.
          </h2>
          <p className="mt-3 md:mt-4 text-sm md:text-lg text-slate-600 max-w-2xl mx-auto leading-snug md:leading-relaxed hidden md:block">
            Step inside <strong>SparkleClean Solutions</strong>, a fully-populated demo of a Canadian commercial cleaning company. Real schedules, real payroll math, live Eva AI insights — all without typing a single setup detail.
          </p>
          <p className="mt-3 md:hidden text-sm text-slate-600 max-w-md mx-auto">
            Tour a real cleaning company demo. Live data. Read-only.
          </p>

          {/* Aggregate live stats — pulled from seeded data so they stay honest */}
          <div className="mt-6 md:mt-8 max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 px-2">
            <Stat icon={Users}      tone="orange"  value="15"      label="Employees scheduled" />
            <Stat icon={DollarSign} tone="emerald" value="$12,174" label="Labor cost tracked"  />
            <Stat icon={Clock}      tone="sky"     value="512 hrs" label="Hours logged"        />
            <Stat icon={TrendingUp} tone="violet"  value="KPI 92.3" label="Avg supervisor score" />
          </div>
        </div>

        {/* The two demo cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-testid="demo-cards-grid">

          {/* ─── Admin / Owner card ─── */}
          <article className="relative rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden" data-testid="demo-card-admin">
            <div className="h-1.5 bg-gradient-to-r from-orange-500 via-rose-400 to-amber-400" />
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-orange-700">
                    <Sparkles size={11} /> Owner / Admin
                  </span>
                  <h3 className="mt-3 text-2xl font-bold text-slate-900">Run the whole business</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                    See the view your operations manager uses every day — payroll, labor analytics, Eva AI alerts, schedules, and more.
                  </p>
                </div>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-orange-50 text-orange-600 shrink-0">
                  <BarChart3 size={20} />
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                <Pill icon={DollarSign} text="Payroll" />
                <Pill icon={BarChart3}  text="Labor dashboard" />
                <Pill icon={Sparkles}   text="Eva AI" />
                <Pill icon={Calendar}   text="Scheduling" />
                <Pill icon={Users}      text="Employees" />
                <Pill icon={Mail}       text="Email center" />
              </div>

              <div className="space-y-2 mb-5">
                <CredChip label="Email"    value="demo-biz@getrestaff.co" testId="demo-admin-email" />
                <CredChip label="Password" value="demo"                   testId="demo-admin-pwd"   />
              </div>

              <Link
                to="/login?demo=biz"
                className="group flex items-center justify-center gap-2 w-full rounded-xl bg-slate-900 hover:bg-orange-500 text-white font-semibold py-3.5 px-5 transition-colors shadow-sm hover:shadow-md"
                data-testid="demo-admin-launch"
              >
                Launch Admin Demo
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </article>

          {/* ─── Staff card ─── */}
          <article className="relative rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 overflow-hidden" data-testid="demo-card-staff">
            <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-400 to-sky-400" />
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                    <CheckSquare size={11} /> Staff Member
                  </span>
                  <h3 className="mt-3 text-2xl font-bold text-slate-900">See it as a worker</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                    Experience what your team sees: personal schedule, clock-in, payslips, tasks, and announcements — straight from their phone.
                  </p>
                </div>
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 shrink-0">
                  <Clock size={20} />
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                <Pill icon={Calendar}     text="My schedule" />
                <Pill icon={Clock}        text="Clock-ins" />
                <Pill icon={DollarSign}   text="Payslips" />
                <Pill icon={CheckSquare}  text="Tasks" />
                <Pill icon={Users}        text="Announcements" />
              </div>

              <div className="space-y-2 mb-5">
                <CredChip label="Email"    value="demo-staff@getrestaff.co" testId="demo-staff-email" />
                <CredChip label="Password" value="demo"                     testId="demo-staff-pwd"   />
              </div>

              <Link
                to="/login?demo=staff"
                className="group flex items-center justify-center gap-2 w-full rounded-xl bg-slate-900 hover:bg-emerald-600 text-white font-semibold py-3.5 px-5 transition-colors shadow-sm hover:shadow-md"
                data-testid="demo-staff-launch"
              >
                Launch Staff Demo
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </article>
        </div>

        {/* Read-only disclaimer */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-800">
            <ShieldCheck size={12} />
            Demo environment is read-only
          </span>
          <span className="text-xs text-slate-500">
            Explore freely — nothing you click can break the demo. Sign up when you're ready for your own tenant.
          </span>
        </div>

        {/* Secondary CTA: full signup */}
        <div className="mt-10 text-center">
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700"
            data-testid="demo-to-signup-link"
          >
            <Eye size={14} />
            Ready to set up your own?  Start your free trial
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
